import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';

function WorkoutModal({ workout, isOpen, onClose }) {
  // State for unit selection
  const [displayUnit, setDisplayUnit] = useState('lbs');
  const KG_TO_LBS = 2.20462;

  if (!workout) return null;

  // Convert weight based on selected unit
  const convertWeight = (weightInKg) => {
    if (!weightInKg) return 0;
    return displayUnit === 'lbs' ? weightInKg * KG_TO_LBS : weightInKg;
  };

  // Wrap the entire modal in a try-catch to prevent crashes
  try {
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        console.error('Date formatting error:', error);
        return dateString;
      }
    };

    const formatDuration = (seconds) => {
      if (!seconds && seconds !== 0) return '-';
      try {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        if (hours > 0) {
          return `${hours}h ${minutes}m ${secs}s`;
        }
        return `${minutes}m ${secs}s`;
      } catch (error) {
        return '-';
      }
    };

    // Format equipment ID to display name
    const formatEquipmentName = (equipmentId) => {
      const equipmentNames = {
        'barbell': 'Barbell',
        'dumbbells': 'Dumbbells',
        'kettlebell': 'Kettlebell',
        'ez-bar': 'EZ Bar',
        'hex-trap-bar': 'Hex Trap Bar',
        'olympic-weight-plates': 'Weight Plates',
        'farmers-walk-handles': 'Farmers Walk Handles',
        'medicine-ball': 'Medicine Ball',
        'battle-ropes': 'Battle Ropes',
        'landmine-attachment': 'Landmine Attachment',
        'sled-pull-harness': 'Sled Pull Harness',
        'sled-pull-straps': 'Sled Pull Straps',
        'flat-bench': 'Flat Bench',
        'incline-bench': 'Incline Bench',
        'decline-bench': 'Decline Bench',
        'jump-rope': 'Jump Rope',
        'smith-machine': 'Smith Machine',
        'leg-press-machine': 'Leg Press Machine',
        'leg-extension-machine': 'Leg Extension Machine',
        'leg-curl-machine': 'Leg Curl Machine (Seated)',
        'hack-squat-machine': 'Hack Squat Machine',
        'chest-press-machine': 'Chest Press Machine',
        'seated-row-machine': 'Seated Row Machine',
        'pec-deck-machine': 'Pec Deck Machine',
        'preacher-curl-bench': 'Preacher Curl Bench',
        'ab-crunch-machine': 'Ab Crunch Machine',
        'lat-pulldown-machine': 'Lat Pulldown Machine',
        'cable-machine': 'Cable Machine',
        'cable-crossover-machine': 'Cable Crossover Machine',
        'shoulder-press-machine': 'Shoulder Press Machine',
        'sled': 'Sled',
        'glute-ham-developer': 'Glute Ham Developer',
        'reverse-hyper-machine': 'Reverse Hyper Machine',
        'hip-abduction-machine': 'Hip Abduction Machine',
        'hip-adduction-machine': 'Hip Adduction Machine',
        'seated-calf-raise-machine': 'Seated Calf Raise Machine',
        'standing-calf-raise-machine': 'Standing Calf Raise Machine',
        'pull-up-assist-machine': 'Pull-up Assist Machine',
        'power-rack': 'Power Rack',
        'sissy-squat-machine': 'Sissy Squat Machine',
        'lateral-raise-machine': 'Lateral Raise Machine',
        'shrug-machine': 'Shrug Machine',
        't-bar-row-machine': 'T-Bar Row Machine',
        'pull-up-bar': 'Pull-up Bar',
        'dip-station': 'Dip Station',
        'adjustable-bench': 'Adjustable Bench',
        'trx-suspension-trainer': 'TRX Suspension Trainer',
        'bosu-ball': 'BOSU Ball',
        'exercise-ball': 'Exercise Ball',
        'ab-wheel': 'Ab Wheel',
        'plyometric-boxes': 'Plyometric Boxes',
        'punching-bag': 'Punching Bag',
        'climbing-rope': 'Climbing Rope',
        'gymnastic-rings': 'Gymnastic Rings',
        'parallettes': 'Parallettes',
        'roman-chair': 'Roman Chair',
        'resistance-bands': 'Resistance Bands',
        'dip-belt': 'Dip Belt',
        'treadmill': 'Treadmill',
        'stationary-bike': 'Stationary Bike',
        'rowing-machine': 'Rowing Machine',
        'elliptical-machine': 'Elliptical Machine',
        'stair-climber': 'Stair Climber',
        'swimming-pool': 'Swimming Pool',
        'assault-bike': 'Assault Bike',
        'ski-erg': 'Ski Erg',
        'bicycle': 'Bicycle',
        'arc-trainer': 'Arc Trainer',
        'foam-roller': 'Foam Roller',
        'massage-gun': 'Massage Gun',
        'lacrosse-ball': 'Lacrosse Ball',
        'yoga-mat': 'Yoga Mat',
        'yoga-blocks': 'Yoga Blocks',
        'stretching-strap': 'Stretching Strap',
        'speed-bag': 'Speed Bag',
        'lying-leg-curl-machine': 'Leg Curl Machine (Lying)',
        'preacher-curl-machine': 'Preacher Curl Machine',
        'pendulum-squat-machine': 'Pendulum Squat Machine',
        'atlantis-lateral-raise-machine': 'Atlantis Lateral Raise Machine',
        'bicep-curl-machine': 'Bicep Curl Machine',
        'freemotion-chest-press-machine': 'FreeMotion Chest Press Machine',
        'hammerstrength-decline-chest-press-machine': 'Hammer Strength Decline Chest Press',
        'hammerstrength-incline-chest-press-machine': 'Hammer Strength Incline Chest Press',
        'glute-kickback-machine': 'Glute Kickback Machine',
        'hurdle-step-over': 'Hurdle Step Over',
        'straight-bar-attachment': 'Straight Bar Attachment',
        'v-bar-attachment': 'V-Bar Attachment',
        'rope-attachment': 'Rope Attachment',
        'd-handle-attachments': 'D-Handle Attachments',
        'ankle-strap': 'Ankle Strap',
        'neutral-grip-pulldown': 'Neutral Grip Pulldown Attachment',
        'lat-pulldown-bar-attachment': 'Lat Pulldown Bar Attachment',
        'captains-chair': "Captain's Chair",
        'hammer-strength-high-row-machine': 'Hammer Strength High Row Machine',
        'hammer-strength-iso-row-machine': 'Hammer Strength Iso Row Machine',
        'pvc-pipe': 'PVC Pipe',
        'handle-bands': 'Handle Bands',
        'mini-loop-bands': 'Mini Loop Bands',
        'tire': 'Tire',
        'yoke': 'Yoke',
        'tricep-extension-machine': 'Tricep Extension Machine'
      };

      return equipmentNames[equipmentId] || equipmentId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const formatRestTime = (seconds) => {
      if (!seconds && seconds !== 0) return null;
      try {
        if (seconds < 60) return `${Math.floor(seconds)}s`;
        const minutes = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
      } catch (error) {
        return null;
      }
    };

    const getStatusBadge = (status) => {
      const variant = status === 'completed' ? 'success' : 'warning';
      return <Badge variant={variant}>{status || 'unknown'}</Badge>;
    };

    // Calculate total volume in selected unit
    const calculateTotalVolume = () => {
      try {
        let totalVolumeKg = 0;
        workout.blocks?.forEach(block => {
          block.exercises?.forEach(exercise => {
            exercise.sets?.forEach(set => {
              if (set.actualWeightKg && set.actualReps) {
                totalVolumeKg += parseFloat(set.actualWeightKg) * parseFloat(set.actualReps);
              }
            });
          });
        });
        // Convert to selected unit
        const totalVolume = displayUnit === 'lbs' ? totalVolumeKg * KG_TO_LBS : totalVolumeKg;
        return Math.round(totalVolume);
      } catch (error) {
        console.error('Error calculating volume:', error);
        return 0;
      }
    };

    // Calculate total sets
    const calculateTotalSets = () => {
      try {
        let totalSets = 0;
        workout.blocks?.forEach(block => {
          block.exercises?.forEach(exercise => {
            totalSets += exercise.sets?.length || 0;
          });
        });
        return totalSets;
      } catch (error) {
        console.error('Error calculating sets:', error);
        return 0;
      }
    };

    const totalVolume = calculateTotalVolume();
    const totalSets = calculateTotalSets();

    const renderSet = (set, setIndex) => {
      const isWarmup = set.setType === 'warmup';

      // Format display based on exercise type
      const formatSetDisplay = () => {
        // Skip if set is invalid
        if (!set) return null;

        // Time-based exercise
        if (set.isTimeBased && set.durationSeconds) {
          const minutes = Math.floor(set.durationSeconds / 60);
          const seconds = set.durationSeconds % 60;
          const timeDisplay = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
          return (
            <>
              <div className="font-medium">{timeDisplay}</div>
              {set.intensity && <div className="text-sm text-muted-foreground">Intensity: {set.intensity}</div>}
            </>
          );
        }

        // Band-based exercise
        if (set.bandColor) {
          return (
            <>
              <div className="font-medium">{set.bandColor} band</div>
              <div className="text-sm text-muted-foreground">{set.actualReps || set.reps || 'n/a'} reps</div>
            </>
          );
        }

        // Bodyweight exercise (no weight, might have intensity)
        if (!set.actualWeightKg && !set.plannedWeightKg && !set.weight && set.intensity) {
          return (
            <>
              <div className="text-sm text-muted-foreground">{set.actualReps || set.reps || 'n/a'} reps</div>
              <div className="text-sm text-muted-foreground">Intensity: {set.intensity}</div>
            </>
          );
        }

        // Check for old data format (weight/reps instead of actualWeightKg/actualReps)
        const weightInKg = set.actualWeightKg || set.weight;
        const reps = set.actualReps || set.reps || set.completedReps;

        // Convert weight to selected unit
        const displayWeight = weightInKg ? convertWeight(weightInKg) : null;
        const plannedDisplayWeight = set.plannedWeightKg ? convertWeight(set.plannedWeightKg) : null;

        // Weight-based exercise (default)
        return (
          <>
            <div className="font-medium">
              {displayWeight ? `${displayWeight.toFixed(1)} ${displayUnit}` :
               plannedDisplayWeight ? `${plannedDisplayWeight.toFixed(1)} ${displayUnit} (planned)` :
               'Bodyweight'}
            </div>
            <div className="text-sm text-muted-foreground">{reps || 'n/a'} reps</div>
          </>
        );
      };

      return (
        <div
          key={setIndex}
          className={`p-3 rounded-lg border ${isWarmup ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-muted/30'}`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-sm">
              Set {setIndex + 1}
              {isWarmup && <Badge variant="warning" className="ml-2 text-xs">Warmup</Badge>}
            </span>
          </div>

          <div className="space-y-1">
            {formatSetDisplay()}

            {/* Show planned vs actual if different */}
            {set.plannedReps && set.plannedReps !== set.actualReps && !set.isTimeBased && (
              <div className="text-xs text-muted-foreground">Planned: {set.plannedReps} reps</div>
            )}
            {set.plannedWeightKg && set.actualWeightKg && set.plannedWeightKg !== set.actualWeightKg && (
              <div className="text-xs text-muted-foreground">Planned: {convertWeight(set.plannedWeightKg).toFixed(1)} {displayUnit}</div>
            )}

            {/* RPE if available */}
            {(set.setRPE || set.targetRPE) && (
              <div className="text-sm">
                <span className="text-orange-500">RPE: {set.setRPE || 'n/a'}</span>
                {set.targetRPE && set.setRPE && set.targetRPE !== set.setRPE && (
                  <span className="text-muted-foreground"> (Target: {set.targetRPE})</span>
                )}
              </div>
            )}

            {/* Rest time if available */}
            {set.actualRestSeconds && (
              <div className="text-xs text-muted-foreground">
                Rest: {formatRestTime(set.actualRestSeconds)}
              </div>
            )}
            {set.plannedRestSeconds && !set.actualRestSeconds && (
              <div className="text-xs text-muted-foreground">
                Planned Rest: {formatRestTime(set.plannedRestSeconds)}
              </div>
            )}
          </div>
        </div>
      );
    };

    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0">
          <DialogHeader className="p-6 pb-0">
            <div className="flex items-center justify-between">
              <DialogTitle>Workout Details</DialogTitle>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Units:</span>
                <Select value={displayUnit} onValueChange={setDisplayUnit}>
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lbs">lbs</SelectItem>
                    <SelectItem value="kg">kg</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </DialogHeader>

          <ScrollArea className="max-h-[calc(90vh-100px)] px-6 pb-6">
            {/* Workout Summary */}
            <Card className="mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Date</p>
                    <p className="text-sm font-medium">{formatDate(workout.workoutStartTime || workout.serverReceivedAt)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Status</p>
                    <div className="mt-1">{getStatusBadge(workout.completionStatus)}</div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Duration</p>
                    <p className="text-sm font-medium">{formatDuration(workout.workoutDurationSeconds)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Source</p>
                    <p className="text-sm font-medium">{workout.workoutSource || 'App'}</p>
                  </div>
                  {workout.workoutRating && (
                    <div>
                      <p className="text-xs text-muted-foreground">Rating</p>
                      <p className="text-sm font-medium text-yellow-500">{'★'.repeat(workout.workoutRating)}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-muted-foreground">Total Volume</p>
                    <p className="text-sm font-medium">{totalVolume > 0 ? `${totalVolume.toLocaleString()} ${displayUnit}` : '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Sets</p>
                    <p className="text-sm font-medium">{totalSets > 0 ? totalSets : '-'}</p>
                  </div>
                  {workout.sessionPerceivedEffort && (
                    <div>
                      <p className="text-xs text-muted-foreground">Perceived Effort</p>
                      <p className="text-sm font-medium">
                        {workout.sessionPerceivedEffort.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-muted-foreground">Voice Coach</p>
                    <div className="mt-1">
                      {workout.voiceCoachEnabled === true || workout.voiceCoachEnabled === 'true' || workout.voiceCoachEnabled === 1 ? (
                        <Badge variant="success">Enabled</Badge>
                      ) : workout.voiceCoachEnabled === false || workout.voiceCoachEnabled === 'false' || workout.voiceCoachEnabled === 0 ? (
                        <Badge variant="secondary">Disabled</Badge>
                      ) : (
                        <Badge variant="muted">Not tracked</Badge>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Apple Watch</p>
                    <div className="mt-1">
                      {workout.appleWatchUsed === true || workout.appleWatchUsed === 'true' || workout.appleWatchUsed === 1 ? (
                        <Badge variant="success">Used</Badge>
                      ) : workout.appleWatchUsed === false || workout.appleWatchUsed === 'false' || workout.appleWatchUsed === 0 ? (
                        <Badge variant="secondary">Not used</Badge>
                      ) : (
                        <Badge variant="muted">Not tracked</Badge>
                      )}
                    </div>
                  </div>
                  {workout.cancellationReason && (
                    <div className="col-span-full">
                      <p className="text-xs text-muted-foreground">Cancellation Reason</p>
                      <p className="text-sm font-medium text-destructive">
                        {workout.cancellationReason.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
                      </p>
                    </div>
                  )}
                </div>

                {/* Equipment List */}
                {workout.gymAvailableEquipment && workout.gymAvailableEquipment.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-muted-foreground mb-2">Available Equipment</p>
                    <div className="flex flex-wrap gap-1">
                      {workout.gymAvailableEquipment.slice(0, 10).map((equipmentId, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {formatEquipmentName(equipmentId)}
                        </Badge>
                      ))}
                      {workout.gymAvailableEquipment.length > 10 && (
                        <Badge variant="outline" className="text-xs">
                          +{workout.gymAvailableEquipment.length - 10} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* User Info */}
            {(workout.userAgeBracket || workout.userAge || workout.userGender || workout.userExperienceLevel || workout.userTrainingGoal) && (
              <Card className="mb-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">User Profile</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {(workout.userAgeBracket || workout.userAge) && (
                      <div>
                        <p className="text-xs text-muted-foreground">Age</p>
                        <p className="text-sm font-medium">{workout.userAgeBracket || workout.userAge}</p>
                      </div>
                    )}
                    {workout.userGender && (
                      <div>
                        <p className="text-xs text-muted-foreground">Gender</p>
                        <p className="text-sm font-medium">{workout.userGender}</p>
                      </div>
                    )}
                    {workout.userExperienceLevel && (
                      <div>
                        <p className="text-xs text-muted-foreground">Experience</p>
                        <p className="text-sm font-medium">{workout.userExperienceLevel}</p>
                      </div>
                    )}
                    {workout.userTrainingGoal && (
                      <div>
                        <p className="text-xs text-muted-foreground">Goal</p>
                        <p className="text-sm font-medium">{workout.userTrainingGoal}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Workout Blocks */}
            <Card className="mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Workout Details</CardTitle>
              </CardHeader>
              <CardContent>
                {!workout.blocks && (
                  <p className="text-muted-foreground text-center py-4">No workout data available. This might be an older workout format.</p>
                )}
                {workout.blocks && workout.blocks.length === 0 && (
                  <p className="text-muted-foreground text-center py-4">No exercises recorded in this workout.</p>
                )}
                {workout.blocks && workout.blocks.length > 0 && (
                  <div className="space-y-6">
                    {workout.blocks.map((block, blockIndex) => {
                      // Additional safety check
                      if (!block) {
                        console.error(`Block at index ${blockIndex} is null/undefined`);
                        return null;
                      }
                      return (
                        <div key={blockIndex} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold">
                              {block.name || `Block ${blockIndex + 1}`}
                              {block.blockType && block.blockType !== 'standard' && (
                                <Badge variant="outline" className="ml-2">{block.blockType}</Badge>
                              )}
                            </h4>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              {block.targetRounds > 1 && (
                                <span>{block.targetRounds} rounds</span>
                              )}
                              {block.restBetweenRounds > 0 && (
                                <span>Rest: {formatRestTime(block.restBetweenRounds)}</span>
                              )}
                            </div>
                          </div>

                          {block.exercises && block.exercises.length > 0 ? (
                            <div className="space-y-4">
                              {block.exercises.map((exercise, exerciseIndex) => (
                                <div key={exerciseIndex} className="border-l-2 border-primary/30 pl-4">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h5 className="font-medium">
                                      {/* Format exercise name from ID */}
                                      {exercise.name ||
                                       (exercise.exerciseID || 'Unknown Exercise')
                                         .replace(/-/g, ' ') // Handle hyphenated IDs
                                         .split(/(?=[A-Z])/)
                                         .join(' ')
                                         .replace(/\b\w/g, c => c.toUpperCase())}
                                    </h5>
                                    {exercise.isReplacement && (
                                      <Badge variant="warning" className="text-xs">
                                        Replaced {exercise.replacedExerciseID && (
                                          <>
                                            {exercise.replacedExerciseID
                                              .replace(/-/g, ' ')
                                              .split(/(?=[A-Z])/)
                                              .join(' ')
                                              .replace(/\b\w/g, c => c.toUpperCase())}
                                          </>
                                        )}
                                        {exercise.replacementReason && (
                                          <span> ({exercise.replacementReason.replace(/_/g, ' ')})</span>
                                        )}
                                      </Badge>
                                    )}
                                    {exercise.wasSkipped && (
                                      <Badge variant="destructive" className="text-xs">
                                        Skipped
                                        {exercise.skipReason && (
                                          <span> ({exercise.skipReason.replace(/_/g, ' ')})</span>
                                        )}
                                      </Badge>
                                    )}
                                  </div>

                                  {/* Sets */}
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    {exercise.sets && exercise.sets.length > 0 ? (
                                      exercise.sets.map((set, setIndex) => {
                                        // Pass exercise properties to set for proper rendering
                                        if (!set) return null;
                                        const enhancedSet = {
                                          ...set,
                                          isTimeBased: set?.isTimeBased || exercise?.isTimeBased,
                                          usesBand: set?.usesBand || exercise?.usesBand
                                        };
                                        return renderSet(enhancedSet, setIndex);
                                      })
                                    ) : (
                                      <div className="col-span-full text-muted-foreground text-sm">No sets recorded</div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-muted-foreground text-sm">No exercises in this block</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* PRs Achieved */}
            {workout.achievedPRs && workout.achievedPRs.length > 0 && (
              <Card className="mb-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Personal Records</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {workout.achievedPRs.map((pr, index) => {
                      // Handle both object PRs and string PRs
                      if (typeof pr === 'string') {
                        return <li key={index} className="text-sm">{pr}</li>;
                      }

                      // Format PR object
                      const exerciseName = pr.exerciseID
                        .split(/(?=[A-Z])/)
                        .join(' ')
                        .replace(/^\w/, c => c.toUpperCase());

                      const prTypeLabel = {
                        'est_1rm': 'Estimated 1RM',
                        'max_weight': 'Max Weight',
                        'total_volume': 'Total Volume',
                        'max_reps': 'Max Reps'
                      }[pr.prType] || pr.prType;

                      const improvementText = pr.percentChange > 0
                        ? `+${pr.percentChange.toFixed(1)}%`
                        : `${pr.percentChange.toFixed(1)}%`;

                      return (
                        <li key={index} className="text-sm">
                          <span className="font-medium">{exerciseName}</span> - {prTypeLabel}: {pr.currentValue.toFixed(1)} (was {pr.previousValue.toFixed(1)})
                          <Badge variant="success" className="ml-2">{improvementText}</Badge>
                        </li>
                      );
                    })}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Additional Info */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Additional Info</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Session ID</p>
                    <p className="text-sm font-mono">{workout.sessionID || workout._id || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Analytics ID</p>
                    <p className="text-sm font-mono">{workout.anonymousUserID || 'N/A'}</p>
                  </div>
                  {workout.totalPausedDuration && (
                    <div>
                      <p className="text-xs text-muted-foreground">Total Paused Time</p>
                      <p className="text-sm font-medium">{formatDuration(workout.totalPausedDuration)}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    );
  } catch (error) {
    console.error('Error rendering workout modal:', error);
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
          </DialogHeader>
          <div className="py-8 text-center">
            <p className="text-muted-foreground mb-2">
              Unable to display workout details. The data may be incomplete or corrupted.
            </p>
            <p className="text-sm text-muted-foreground">
              Error: {error.message}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
}

export default WorkoutModal;
